/**
 * The task's metadata list: who proposed, holds and approved it, the
 * submitted Data Room document pinned to its revision, and the timestamps.
 */

import type { ReactNode } from 'react';
import { Link } from 'react-router-dom';

import type { TaskDetail } from '../../api.js';
import { EventTime, useWorkspaceCopy } from '../../components.js';
import { workspaceSlots } from '../../slots.js';

export function TaskMetadata({ task }: { readonly task: TaskDetail }): ReactNode {
  const copy = useWorkspaceCopy();

  // Normalised, because the server omits the field for roles the Data Room
  // refuses — absent and "no document" must render identically.
  const submittedDocument = task.submittedDocument ?? null;

  return (
    <dl className="mj-task-meta">
      <dt>{copy.task.proposedBy}</dt>
      <dd>{task.proposedBy.displayName}</dd>
      <dt>{copy.task.assignee}</dt>
      <dd>{task.assignee?.displayName ?? copy.task.unassigned}</dd>
      {task.approvedBy !== null ? (
        <>
          <dt>{copy.task.approvedBy}</dt>
          <dd>{task.approvedBy.displayName}</dd>
        </>
      ) : null}
      {submittedDocument !== null ? (
        <>
          <dt>{copy.task.submittedDocumentLabel}</dt>
          <dd>
            <Link
              to={`/data-room/${submittedDocument.documentId}`}
              style={{ color: 'var(--text-link)' }}
            >
              {submittedDocument.title}
            </Link>{' '}
            <span style={{ color: 'var(--text-muted)' }}>
              {copy.task.submittedRevision(submittedDocument.revisionVersion)}
            </span>{' '}
            {/* The join that produced this reference was already bounded
                      by the reader's audiences, so naming the audience here
                      discloses nothing they were not already shown. */}
            {workspaceSlots().documentBadge?.({
              audience: submittedDocument.audience,
            })}
            {submittedDocument.deleted ? (
              <span
                style={{
                  display: 'block',
                  font: 'var(--type-caption)',
                  color: 'var(--text-muted)',
                  marginTop: 2,
                }}
              >
                {copy.task.submittedDocumentDeleted}
              </span>
            ) : null}
          </dd>
        </>
      ) : null}
      <dt>{copy.task.created}</dt>
      <dd>
        <EventTime iso={task.createdAt} />
      </dd>
      <dt>{copy.task.updated}</dt>
      <dd>
        <EventTime iso={task.updatedAt} />
      </dd>
    </dl>
  );
}
